import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, MapPin, Navigation, CalendarClock, X } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

export interface CorporateTicket {
  id: string;
  code: string;
  pickup: { address: string | null; lat: number; lng: number };
  dropoff: { address: string | null; lat: number; lng: number };
  stops: { address?: string }[];
  rideType: string | null;
  price: number;
  /** Why the company approved it. Null on tickets issued before this was captured. */
  reason: string | null;
  scheduledFor: string | null;
  expiresAt: string;
  secondsLeft: number;
}

/** "5h 12m" / "48m" / "expired" - the unit that matters at that distance out. */
function timeLeft(secs: number): string {
  if (secs <= 0) return 'expired';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m left`;
  if (m >= 1) return `${m}m left`;
  return 'less than a minute left';
}

/**
 * The rider's approved-but-not-yet-travelled corporate ride tickets.
 *
 * Her company approved this exact trip in advance and the fare is already held
 * against their credit, so there is nothing left to negotiate: tapping "Use
 * ticket" dispatches a driver for the route on the ticket, at the price on the
 * ticket. That is the whole point of the feature - the approval happened when
 * her approver had a moment, and the journey happens when she is ready.
 *
 * Renders nothing at all when she has no live tickets, so it costs the home
 * screen nothing for the overwhelming majority of riders who are not corporate.
 */
const CorporateTickets: React.FC<{
  /** Adopt the dispatched ride and drop into the normal searching UI. */
  onRedeemed: (rideId: string) => void;
}> = ({ onRedeemed }) => {
  const [tickets, setTickets] = useState<CorporateTicket[]>([]);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState<CorporateTicket | null>(null);
  // Handing a ticket back cannot be undone - it needs a fresh approval - so it
  // gets its own confirmation rather than firing straight off the card.
  const [returning, setReturning] = useState<CorporateTicket | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const res = await fetch(`${API_URL}/corporate/tickets`, {
        headers: { Authorization: `Bearer ${token}` }, signal,
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (!signal?.aborted) setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
    } catch {
      // Offline, or tickets not switched on yet. Either way: show nothing rather
      // than an error the rider can do nothing about.
      if (!signal?.aborted) setTickets([]);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    // Slow poll. A ticket changes state on a scale of hours, and the only urgent
    // transition (she redeems it) is driven from this component anyway.
    const id = setInterval(() => load(), 60_000);
    return () => { ctrl.abort(); clearInterval(id); };
  }, [load]);

  // Local countdown so the card stays honest between polls.
  useEffect(() => {
    if (!tickets.length) return;
    const id = setInterval(() => {
      setTickets((prev) => prev
        .map((t) => ({ ...t, secondsLeft: Math.max(0, t.secondsLeft - 1) }))
        // Drop it the moment it dies rather than offering a button that will be
        // refused - the server expires it on its next read regardless.
        .filter((t) => t.secondsLeft > 0));
    }, 1000);
    return () => clearInterval(id);
  }, [tickets.length]);

  const redeem = async (ticket: CorporateTicket) => {
    setBusyId(ticket.id);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/corporate/tickets/${ticket.id}/redeem`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Could not call a driver just now.');
      setConfirming(null);
      setTickets((prev) => prev.filter((t) => t.id !== ticket.id));
      onRedeemed(data.rideId);
    } catch (e: any) {
      setError(e?.message || 'Could not call a driver just now.');
      // Re-read: the ticket may have expired or been used on another device, and
      // the list should reflect that rather than keep offering it.
      load();
    }
    setBusyId('');
  };

  const giveBack = async (ticket: CorporateTicket) => {
    setBusyId(ticket.id);
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_URL}/corporate/tickets/${ticket.id}/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      setTickets((prev) => prev.filter((t) => t.id !== ticket.id));
      setConfirming(null);
      setReturning(null);
    } catch { /* the next poll corrects it */ }
    setBusyId('');
  };

  if (!tickets.length) return null;

  return (
    <>
      <div className="space-y-2">
        {tickets.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-safaricab-gold rounded-2xl overflow-hidden shadow-sm"
          >
            <div className="flex items-center justify-between px-4 py-2.5 bg-safaricab-gold/15">
              <div className="flex items-center gap-2 min-w-0">
                <Ticket className="w-4 h-4 text-safaricab-gold flex-shrink-0" />
                <span className="text-gray-900 font-bold text-xs tracking-wide truncate">
                  RIDE TICKET · {t.code}
                </span>
              </div>
              <span className={`text-[11px] font-semibold flex-shrink-0 ${t.secondsLeft < 3600 ? 'text-red-500' : 'text-gray-500'}`}>
                {timeLeft(t.secondsLeft)}
              </span>
            </div>

            <div className="px-4 py-3 space-y-1.5">
              <p className="flex items-center gap-2 text-gray-800 text-sm">
                <MapPin className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                <span className="truncate">{t.pickup.address || 'Pickup'}</span>
              </p>
              <p className="flex items-center gap-2 text-gray-800 text-sm">
                <Navigation className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <span className="truncate">{t.dropoff.address || 'Drop-off'}</span>
              </p>
              {/* What the company actually approved it FOR - worth showing back
                  to her, since redeeming it for something else is the thing that
                  turns up in a month-end query with her name on it. */}
              {t.reason && (
                <p className="text-gray-500 text-[11px] pt-0.5">
                  Approved for: <span className="text-gray-700 font-medium">{t.reason}</span>
                </p>
              )}
              {t.scheduledFor && (
                <p className="flex items-center gap-2 text-gray-400 text-[11px]">
                  <CalendarClock className="w-3 h-3 flex-shrink-0" />
                  You planned around {new Date(t.scheduledFor).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' '}but you can go whenever you like
                </p>
              )}
            </div>

            <div className="px-4 pb-3 space-y-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setConfirming(t)}
                  disabled={!!busyId}
                  className="flex-1 bg-safaricab-gold disabled:opacity-60 text-black font-bold text-sm py-3 rounded-xl flex items-center justify-center gap-2"
                >
                  {/* The same mark as the Get Started button on the auth screen,
                      not a generic car glyph - it is the app's own icon and it
                      sits on the same gold-with-black-artwork button there. Sized
                      against the label the way that one is (h-6 next to text-lg,
                      so h-5 next to text-sm). */}
                  <img src="/icons/getstarted.png" alt="" className="h-5 w-auto" /> Use ticket, call driver
                </button>
                <span className="text-gray-900 font-bold text-sm flex-shrink-0">
                  KES {t.price.toLocaleString()}
                </span>
              </div>
              {/* Giving the ticket back is a decision of its own, so it gets its
                  own visible control rather than hiding inside the "call a
                  driver" confirmation - somebody who has decided NOT to travel
                  should never have to open the flow for travelling to say so.
                  It is the ONLY way a ticket leaves early: cancelling the trip
                  hands it straight back, and otherwise it simply runs out. */}
              <button
                onClick={() => setReturning(t)}
                disabled={!!busyId}
                className="w-full text-gray-500 text-[11px] py-1.5 disabled:opacity-60"
              >
                Return ticket, I no longer need this trip
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Redeeming summons a real driver to a real kerb, so it gets a deliberate
          second tap. The same sheet carries "give it back", because the moment
          she realises she does not need it is the moment she is looking at it -
          and cancelling frees her company's held credit immediately. */}
      <AnimatePresence>
        {confirming && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[260] bg-black/70 flex items-end sm:items-center justify-center p-4"
            onClick={() => !busyId && setConfirming(null)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-sm p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="text-gray-900 font-bold text-lg">Call a driver now?</h3>
                <button onClick={() => !busyId && setConfirming(null)} aria-label="Close"
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <p className="text-gray-500 text-sm mb-4">
                We'll find you a driver for {confirming.dropoff.address?.split(',')[0] || 'your destination'} straight away.
                Your company already approved this trip at KES {confirming.price.toLocaleString()} , so there's nothing to pay the driver.
              </p>

              {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

              <button
                onClick={() => redeem(confirming)}
                disabled={!!busyId}
                className="w-full bg-safaricab-gold disabled:opacity-60 text-black font-bold py-3.5 rounded-xl mb-2"
              >
                {busyId ? 'Finding a driver…' : 'Yes, call a driver'}
              </button>
              {/* Returning the ticket used to live here. It moved out to the
                  card: someone who has decided not to travel should not have to
                  open "call a driver" to say so, and having it one tap from the
                  confirm button made it easy to hit by accident. */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Returning a ticket is the one action here that cannot be undone - the
          approval is spent and a new one has to be requested - so it is spelled
          out rather than confirmed with a bare "are you sure?". */}
      <AnimatePresence>
        {returning && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[260] bg-black/70 flex items-end sm:items-center justify-center p-4"
            onClick={() => !busyId && setReturning(null)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-sm p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="text-gray-900 font-bold text-lg">Return this ticket?</h3>
                <button onClick={() => !busyId && setReturning(null)} aria-label="Close"
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <p className="text-gray-500 text-sm mb-4">
                {returning.code} will be given up and the KES {returning.price.toLocaleString()} released back to your company.
                You'll need a fresh approval to take this trip later.
              </p>
              <p className="text-gray-400 text-[11px] mb-4">
                If you're only unsure about the timing, just leave it, it lasts until{' '}
                {new Date(returning.expiresAt).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                {' '}and disappears on its own.
              </p>
              <button
                onClick={() => giveBack(returning)}
                disabled={!!busyId}
                className="w-full bg-gray-900 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl mb-2"
              >
                {busyId ? 'Returning…' : 'Yes, return it'}
              </button>
              <button
                onClick={() => setReturning(null)}
                disabled={!!busyId}
                className="w-full text-gray-500 text-xs py-2"
              >
                Keep my ticket
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CorporateTickets;
